<!--
  Reusable audit-log table. Drop it under any entity detail page:
    <audit-log-table entity="user" :id="$route.params.userId" />
  It fetches GET /api/auditlog/:entity/:id and renders the same "Log" card used
  by the operation views (Date / Who / Operation / Message / Details).
-->
<template>
  <div class="vx-row" v-if="getlog.length > 0">
    <div class="vx-col lg:w-full w-full">
      <vx-card :title="title" class="mb-base">
        <div class="overflow-x-auto">
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #D1D5DB;">
            <thead style="background-color: #F9FAFB;">
              <tr style="border: 1px solid #D1D5DB;">
                <th style="padding: 0.75rem; text-align: left; font-size: 0.75rem; font-weight: 500; text-transform: uppercase; color: #6B7280; border: 1px solid #D1D5DB;">Date</th>
                <th style="padding: 0.75rem; text-align: left; font-size: 0.75rem; font-weight: 500; text-transform: uppercase; color: #6B7280; border: 1px solid #D1D5DB;">Who</th>
                <th style="padding: 0.75rem; text-align: left; font-size: 0.75rem; font-weight: 500; text-transform: uppercase; color: #6B7280; border: 1px solid #D1D5DB;">Operation</th>
                <th style="padding: 0.75rem; text-align: left; font-size: 0.75rem; font-weight: 500; text-transform: uppercase; color: #6B7280; border: 1px solid #D1D5DB;">Message</th>
                <th style="padding: 0.75rem; text-align: left; font-size: 0.75rem; font-weight: 500; text-transform: uppercase; color: #6B7280; border: 1px solid #D1D5DB;">Details</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(log, index) in getlog" :key="index" style="border: 1px solid #D1D5DB;">
                <td style="padding: 0.75rem; border: 1px solid #D1D5DB; white-space: nowrap;">{{ log.date }}</td>
                <td style="padding: 0.75rem; border: 1px solid #D1D5DB; white-space: nowrap;">{{ log.who }}</td>
                <td style="padding: 0.75rem; border: 1px solid #D1D5DB; white-space: nowrap;">
                  <span v-if="log.idoperation">#{{ log.idoperation }}</span>
                </td>
                <td style="padding: 0.75rem; border: 1px solid #D1D5DB; white-space: nowrap;">{{ log.message }}</td>
                <td style="padding: 0.75rem; border: 1px solid #D1D5DB; white-space: normal;">
                  <!-- Field-level modifications. `.length` matters: an empty
                       array is truthy and used to blank the whole cell. -->
                  <template v-if="log.modifications && log.modifications.length">
                    <div v-for="(mod, modIndex) in log.modifications" :key="modIndex" style="margin-bottom: 0.5rem;">
                      <strong>{{ mod.champ }}:</strong>
                      <span style="margin-left: 0.25rem; color: red; text-decoration: line-through;" v-html="mod.avant"></span>
                      <span style="margin: 0 0.25rem;">&rarr;</span>
                      <span style="color: green;" v-html="mod.apres"></span>
                    </div>
                  </template>
                  <!-- Quotation attached to / detached from a transport -->
                  <template v-else-if="log.quotation_id && !log.quotations">
                    <div>
                      <strong :style="{ color: log.message && log.message.includes('detach') ? 'red' : 'green' }">
                        Quotation REF #{{ log.quotation_id }}
                      </strong>
                      <span v-if="log.aviz_serie"> &mdash; Aviz: {{ log.aviz_serie }}/{{ log.aviz_number }}</span>
                    </div>
                  </template>
                  <!-- Mass attach / detach -->
                  <template v-else-if="log.quotations">
                    <div>
                      <strong>Quotations:</strong>
                      <span v-for="(q, qi) in log.quotations" :key="qi" style="margin-left: 0.25rem;">REF #{{ q }}<span v-if="qi < log.quotations.length - 1">, </span></span>
                      <span v-if="log.aviz_serie"> &mdash; Aviz série: {{ log.aviz_serie }}</span>
                    </div>
                  </template>
                  <!-- Transport cost split across quotations -->
                  <template v-else-if="log.total_cost">
                    <div>
                      <strong>Total cost:</strong> {{ log.total_cost }} EUR &mdash;
                      <strong>Total volume:</strong> {{ log.total_volume }} m³
                    </div>
                  </template>
                  <!-- Product added -->
                  <template v-else-if="log.createdProduct">
                    <div>
                      <strong style="color: green;">Product Added:</strong>
                      <span style="color: green;"> {{ log.createdProduct.description }}</span>
                    </div>
                  </template>
                  <!-- Product deleted -->
                  <template v-else-if="log.deleteResult !== undefined">
                    <div>
                      <strong style="color: red; text-decoration: line-through;">Deleted</strong>
                    </div>
                  </template>
                  <!-- Top-level avant/apres -->
                  <template v-else-if="log.avant !== undefined || log.apres !== undefined">
                    <div>
                      <span style="color: red; text-decoration: line-through;">{{ log.avant }}</span>
                      <span style="margin: 0 0.25rem;">&rarr;</span>
                      <span style="color: green;">{{ log.apres }}</span>
                    </div>
                  </template>
                  <!-- Free-text details -->
                  <template v-else-if="log.details">{{ log.details }}</template>
                  <!-- No extra details for this entry -->
                  <template v-else>
                    <span style="color: #9CA3AF; font-style: italic;">&mdash;</span>
                  </template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </vx-card>
    </div>
  </div>
</template>

<script>
import axios from "@/axios.js";

export default {
  name: "AuditLogTable",
  props: {
    entity: { type: String, required: true },
    id: { type: [String, Number], required: true },
    title: { type: String, default: "Log" },
  },
  data() {
    return { getlog: [] };
  },
  watch: {
    id() {
      this.fetchLog();
    },
  },
  methods: {
    fetchLog() {
      if (this.id === null || this.id === undefined || this.id === "") return;
      axios
        .get("/auditlog/" + this.entity + "/" + this.id)
        .then((response) => {
          this.getlog = [...response.data];
        })
        .catch(() => {
          /* no log yet / unreadable: keep the table hidden */
        });
    },
  },
  mounted() {
    this.fetchLog();
  },
};
</script>
